import React, { useState, useEffect, useRef } from 'react';
import {
    GitPullRequest, RefreshCw, AlertCircle, CheckCircle2,
    Loader2, Users, Briefcase, Zap, BarChart2, Link2, ShieldCheck, Clock
} from 'lucide-react';
import { supabase } from '../services/supabaseService';
import { testBullhornConnection, fetchBullhornJobSubmissions, fetchBullhornJobNotes, fetchBullhornJobOrdersRaw } from '../services/bullhornService';
import { supabase_sync_bullhorn_job_submissions, supabase_sync_bullhorn_vacancies, supabase_sync_bullhorn_vacancy_dateAdded_only } from '../services/supabaseService';

interface Props {
    user: any;
}

interface Credentials {
    clientId: string;
    clientSecret: string;
    apiUsername: string;
    apiPassword: string;
}

interface VacancyRow {
    id: string;
    title: string;
    bullhorn_id: string | number;
    submissionCount?: number;
    noteCount?: number;
    synced?: number;
    status?: 'idle' | 'syncing' | 'done' | 'error';
    error?: string;
}

interface LogEntry {
    time: string;
    msg: string;
    type: 'info' | 'success' | 'error' | 'warn';
}

const BullhornPipelineSync: React.FC<Props> = ({ user }) => {
    // ── Credentials loaded from DB (same as BullhornIntegration) ────
    const [credentials, setCredentials] = useState<Credentials | null>(null);
    const [loadingCreds, setLoadingCreds] = useState(true);

    useEffect(() => {
        const fetchCreds = async () => {
            if (!user?.clientId) return;
            setLoadingCreds(true);
            try {
                const { data } = await supabase
                    .from('clients')
                    .select('bullhorn_settings')
                    .eq('id', user.clientId)
                    .single();
                if (data?.bullhorn_settings?.clientId) {
                    setCredentials(data.bullhorn_settings);
                }
            } catch (e) {
                console.error('Failed to load Bullhorn creds:', e);
            }
            setLoadingCreds(false);
        };
        fetchCreds();
    }, [user?.clientId]);

    // ── Auth state ──────────────────────────────────────────────────
    const [authData, setAuthData] = useState<{ restUrl: string; BhRestToken: string } | null>(null);
    const authDataRef = useRef<{ restUrl: string; BhRestToken: string } | null>(null); // live ref for sync loop
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectError, setConnectError] = useState<string | null>(null);
    const [connectSuccess, setConnectSuccess] = useState(false);

    // Keep authDataRef in sync with state
    useEffect(() => { authDataRef.current = authData; }, [authData]);

    // ── Re-auth helper (called automatically on 401) ─────────────────
    const reAuth = async (): Promise<{ restUrl: string; BhRestToken: string } | null> => {
        if (!credentials) return null;
        addLog('🔄 Token verlopen (401) — herverbinden met Bullhorn...', 'warn');
        try {
            const result = await testBullhornConnection({
                clientId: credentials.clientId,
                clientSecret: credentials.clientSecret,
                apiUsername: credentials.apiUsername,
                apiPassword: credentials.apiPassword,
            });
            const fresh = { restUrl: result.restUrl, BhRestToken: result.BhRestToken };
            setAuthData(fresh);
            authDataRef.current = fresh;
            addLog('✓ Herverbonden — nieuw token actief.', 'success');
            return fresh;
        } catch (e: any) {
            addLog(`✗ Herverbinden mislukt: ${e.message}`, 'error');
            return null;
        }
    };

    // ── Sync state ──────────────────────────────────────────────────
    const [vacancies, setVacancies] = useState<VacancyRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [existingAppCount, setExistingAppCount] = useState<number>(0);
    const [totalSynced, setTotalSynced] = useState(0);
    const [totalSubmissions, setTotalSubmissions] = useState(0);
    const [totalNotes, setTotalNotes] = useState(0);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [skippedCount, setSkippedCount] = useState(0);
    const [forceAll, setForceAll] = useState(false); // re-sync already-synced vacancies
    const stopRef = useRef(false);

    const addLog = (msg: string, type: LogEntry['type'] = 'info') => {
        const time = new Date().toLocaleTimeString('nl-NL');
        setLogs(prev => [{ time, msg, type }, ...prev].slice(0, 500));
    };

    // ── Step 1: Connect / re-auth ────────────────────────────────────
    const handleConnect = async () => {
        if (!credentials) {
            setConnectError('Geen Bullhorn credentials gevonden. Ga naar Bullhorn Sync Settings om ze in te stellen.');
            return;
        }
        setIsConnecting(true);
        setConnectError(null);
        setConnectSuccess(false);
        try {
            addLog('🔐 Verbinden met Bullhorn API...', 'info');
            const result = await testBullhornConnection({
                clientId: credentials.clientId,
                clientSecret: credentials.clientSecret,
                apiUsername: credentials.apiUsername,
                apiPassword: credentials.apiPassword,
            });
            const fresh = { restUrl: result.restUrl, BhRestToken: result.BhRestToken };
            setAuthData(fresh);
            authDataRef.current = fresh;
            setConnectSuccess(true);
            addLog('✓ Verbonden! Token ontvangen.', 'success');
            await loadVacancies(fresh);
        } catch (e: any) {
            let msg = e.message || 'Onbekende fout';
            try { if (msg.startsWith('{')) msg = JSON.parse(msg)?.message || msg; } catch {}
            setConnectError(msg);
            addLog(`✗ Verbinding mislukt: ${msg}`, 'error');
        }
        setIsConnecting(false);
    };

    // ── Step 2: Load vacancies (paginated + deduplicated) ────────────
    const loadVacancies = async (auth?: { restUrl: string; BhRestToken: string }) => {
        const activeAuth = auth || authData;
        if (!activeAuth) return;
        setLoading(true);
        try {
            // -- 0. Live Sync All Vacancies from Bullhorn first
            addLog('🔎 Alle vacatures ophalen uit Bullhorn...', 'info');
            try {
                const rawVacs = await fetchBullhornJobOrdersRaw(activeAuth, "isDeleted:0", true, true, (msg) => addLog(msg, 'info'));
                addLog(`✅ ${rawVacs.data.length} vacatures uit Bullhorn gehaald. Starten met opslaan in database...`, 'info');
                
                const { data: mappedClients } = await supabase.from('clients').select('id, bullhorn_id').not('bullhorn_id', 'is', null);
                
                const syncResult = await supabase_sync_bullhorn_vacancies(user.clientId, rawVacs.data, mappedClients || [], (msg) => addLog(msg, 'info'));
                
                if (syncResult.success) {
                    addLog(`🎉 Alle Bullhorn vacatures succesvol gesynchroniseerd met Werftools! (${syncResult.count || 0} verwerkt)`, 'success');
                } else {
                    addLog(`❌ Fout bij opslaan vacatures in database: ${syncResult.error}`, 'error');
                }
            } catch (err: any) {
                addLog(`❌ Misslukt om vacatures uit Bullhorn op te halen: ${err.message}`, 'error');
            }

            // Paginate through ALL rows (Supabase default limit is 1000)
            let allRows: any[] = [];
            let from = 0;
            const PAGE = 1000;
            while (true) {
                const { data, error } = await supabase
                    .from('vacancies')
                    .select('id, title, bullhorn_id, created_at, vacancy_number')
                    .not('bullhorn_id', 'is', null)
                    .order('vacancy_number', { ascending: false, nullsFirst: false })
                    .range(from, from + PAGE - 1);

                if (error) throw error;
                if (!data || data.length === 0) break;
                allRows = allRows.concat(data);
                if (data.length < PAGE) break;
                from += PAGE;
            }

            // Deduplicate by bullhorn_id — keep the first (most recent created_at) per ID
            const seen = new Set<string>();
            const unique: any[] = [];
            let dupCount = 0;
            for (const row of allRows) {
                const key = String(row.bullhorn_id).trim();
                if (!seen.has(key)) {
                    seen.add(key);
                    unique.push(row);
                } else {
                    dupCount++;
                }
            }

            const { count: appCount } = await supabase
                .from('applications')
                .select('id', { count: 'exact', head: true });

            setExistingAppCount(appCount || 0);
            setVacancies(unique.map((v: any) => ({ ...v, status: 'idle' })));

            const dupMsg = dupCount > 0 ? ` (⚠️ ${dupCount} duplicaten weggefilterd — ${allRows.length} rijen totaal)` : '';
            addLog(`📋 ${unique.length} unieke vacatures met Bullhorn ID${dupMsg}. Applications in DB: ${appCount || 0}`, dupCount > 0 ? 'warn' : 'info');
        } catch (e: any) {
            addLog(`Fout bij laden vacatures: ${e.message}`, 'error');
        }
        setLoading(false);
    };

    const syncDateAddedOnly = async () => {
        if (!authDataRef.current) return;
        addLog('🕒 Alleen Bullhorn dateAdded syncen (veilig)...', 'info');
        try {
            const rawVacs = await fetchBullhornJobOrdersRaw(authDataRef.current, "isDeleted:0", true, true, (msg) => addLog(msg, 'info'));
            const res = await supabase_sync_bullhorn_vacancy_dateAdded_only(user.clientId, rawVacs.data, (msg) => addLog(msg, 'info'));
            if (res.success) addLog(`✅ dateAdded bijgewerkt voor ${res.count || 0} vacatures.`, 'success');
            else addLog(`❌ dateAdded sync fout: ${res.error}`, 'error');
        } catch (e: any) {
            addLog(`❌ dateAdded sync mislukt: ${e.message}`, 'error');
        }
    };


    // ── Step 3: Run sync – skip already synced, parallel batches of 5 ─
    const runSync = async () => {
        if (!authDataRef.current || vacancies.length === 0) return;
        setRunning(true);
        stopRef.current = false;
        setTotalSynced(0);
        setTotalSubmissions(0);
        setTotalNotes(0);
        setSkippedCount(0);
        setCurrentIdx(0);

        // ── 1. Pre-load existing application counts from Supabase ────
        addLog('⚡ Pre-loading bestaande applications...', 'info');
        const existingMap = new Map<string, number>(); // vacancy_id → app count
        try {
            const vacIds = vacancies.map(v => v.id).filter(Boolean);
            const CHUNK = 20; // Reduced from 200 to 20 to prevent URL overflow 400 errors
            for (let ci = 0; ci < vacIds.length; ci += CHUNK) {
                const chunk = vacIds.slice(ci, ci + CHUNK);
                const { data } = await supabase
                    .from('applications')
                    .select('vacancy_id')
                    .in('vacancy_id', chunk);
                if (data) {
                    data.forEach((r: any) => {
                        existingMap.set(r.vacancy_id, (existingMap.get(r.vacancy_id) || 0) + 1);
                    });
                }
            }
        } catch (e: any) {
            addLog(`⚠️ Pre-load fout (gaat toch door): ${e.message}`, 'warn');
        }

        const toSync = forceAll
            ? vacancies
            : vacancies.filter(v => !existingMap.has(v.id));
        const skipped = vacancies.length - toSync.length;
        setSkippedCount(skipped);

        addLog(`🚀 Sync gestart: ${toSync.length} te verwerken, ${skipped} overgeslagen (al gesynchroniseerd).`, 'info');

        let synced = 0;
        let subTotal = 0;
        let noteTotal = 0;
        let processed = 0;

        // Keepalive ping every 8 minutes
        const keepaliveInterval = setInterval(async () => {
            const auth = authDataRef.current;
            if (!auth) return;
            try {
                const apiUrl = window.location.hostname !== 'localhost' ? '' : 'http://localhost:4000';
                await fetch(`${apiUrl}/api/bullhorn/proxy`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ restUrl: auth.restUrl, BhRestToken: auth.BhRestToken, endpoint: 'ping', method: 'GET' })
                });
            } catch {}
        }, 8 * 60 * 1000);

        const is401 = (e: any) => { const m = e?.message || ''; return m.includes('401') || m.includes('Unauthorized'); };
        const is429 = (e: any) => { const m = e?.message || ''; return m.includes('429') || m.includes('requestThrottled') || m.includes('capacity exceeded'); };

        const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

        const fetchVacData = async (vac: VacancyRow, attempt = 0): Promise<{ submissions: any[]; notes: any[] }> => {
            const auth = authDataRef.current!;
            try {
                const [submissions, notes] = await Promise.all([
                    fetchBullhornJobSubmissions(auth, vac.bullhorn_id),
                    fetchBullhornJobNotes(auth, vac.bullhorn_id),
                ]);
                return { submissions, notes };
            } catch (e: any) {
                if (is401(e) && attempt === 0) {
                    const fresh = await reAuth();
                    if (fresh) return fetchVacData(vac, attempt + 1);
                }
                if (is429(e) && attempt < 3) {
                    const waitSec = Math.pow(2, attempt + 2); // 4s, 8s, 16s
                    addLog(`⏳ Rate limit (429) — wachten ${waitSec}s voor retry #${attempt + 1}...`, 'warn');
                    await sleep(waitSec * 1000);
                    return fetchVacData(vac, attempt + 1);
                }
                throw e;
            }
        };

        const processOne = async (vac: VacancyRow) => {
            if (stopRef.current) return;
            try {
                const { submissions, notes } = await fetchVacData(vac);

                // Submissions
                let syncedCount = 0;
                if (submissions.length > 0) {
                    const result = await supabase_sync_bullhorn_job_submissions(user.clientId, vac.id, submissions);
                    syncedCount = result.success ? (result.count || 0) : 0;
                }

                // Notes
                if (notes.length > 0) {
                    const notesPayload = notes.map((n: any) => ({
                        id: n.id, action: n.action, comments: n.comments, dateAdded: n.dateAdded,
                        author: n.commentingPerson ? `${n.commentingPerson.firstName || ''} ${n.commentingPerson.lastName || ''}`.trim() : 'Onbekend',
                        candidateName: n.personReference ? `${n.personReference.firstName || ''} ${n.personReference.lastName || ''}`.trim() : '',
                    }));
                    const { data: ev } = await supabase.from('vacancies').select('data').eq('id', vac.id).single();
                    await supabase.from('vacancies').update({ data: { ...(ev?.data || {}), bullhornNotes: notesPayload } }).eq('id', vac.id);
                }

                return { submissions: submissions.length, notes: notes.length, syncedCount };
            } catch (e: any) {
                addLog(`  ✗ ${vac.title}: ${e.message}`, 'error');
                setVacancies(prev => prev.map(v => v.id === vac.id ? { ...v, status: 'error', error: e.message } : v));
                return { submissions: 0, notes: 0, syncedCount: 0 };
            }
        };

        // ── Throttled batch processing (voorkomt 429 rate limit) ─────────
        const BATCH = 2; // Max 2 parallel om 429 te voorkomen
        const INTER_BATCH_DELAY = 800; // ms tussen batches
        for (let i = 0; i < toSync.length; i += BATCH) {
            if (stopRef.current) {
                addLog(`⏹ Sync gestopt na ${processed} vacatures.`, 'warn');
                break;
            }
            const batch = toSync.slice(i, i + BATCH);
            setCurrentIdx(i);

            // Mark batch as syncing
            setVacancies(prev => prev.map(v =>
                batch.some(b => b.id === v.id) ? { ...v, status: 'syncing' } : v
            ));

            const results = await Promise.all(batch.map(vac => processOne(vac)));

            results.forEach((r, bi) => {
                if (!r) return;
                subTotal += r.submissions;
                noteTotal += r.notes;
                synced += r.syncedCount;
                processed++;
            });

            setTotalSubmissions(subTotal);
            setTotalNotes(noteTotal);
            setTotalSynced(synced);

            // Mark batch as done
            setVacancies(prev => prev.map(v =>
                batch.some(b => b.id === v.id) && v.status !== 'error'
                    ? { ...v, status: 'done', submissionCount: results[batch.findIndex(b => b.id === v.id)]?.submissions, synced: results[batch.findIndex(b => b.id === v.id)]?.syncedCount }
                    : v
            ));

            if (processed % 20 === 0) {
                addLog(`⚡ ${processed}/${toSync.length} verwerkt — ${synced} nieuw, ${subTotal} submissions`, 'info');
            }

            // Throttle: wacht tussen batches om rate limit te respecteren
            if (i + BATCH < toSync.length) await sleep(INTER_BATCH_DELAY);
        }

        clearInterval(keepaliveInterval);
        const { count: newCount } = await supabase.from('applications').select('id', { count: 'exact', head: true });
        setExistingAppCount(newCount || 0);
        addLog(`🎉 Klaar! ${synced} nieuw gesynchroniseerd, ${subTotal} submissions, ${skipped} overgeslagen.`, 'success');
        setRunning(false);
    };

    const statusColor: Record<string, string> = {
        idle:    'bg-slate-100 text-slate-500',
        syncing: 'bg-blue-100 text-blue-700 animate-pulse',
        done:    'bg-emerald-100 text-emerald-700',
        error:   'bg-red-100 text-red-600',
    };
    const statusLabel: Record<string, string> = {
        idle: '·', syncing: 'Bezig...', done: 'Klaar', error: 'Fout',
    };

    const progress = vacancies.length > 0 ? Math.round(((currentIdx + 1) / vacancies.length) * 100) : 0;
    const doneCount = vacancies.filter(v => v.status === 'done' || v.status === 'error').length;

    if (loadingCreds) {
        return (
            <div className="flex items-center justify-center h-full p-12 text-slate-400">
                <Loader2 size={24} className="animate-spin mr-3" /> Bullhorn instellingen laden...
            </div>
        );
    }

    if (!credentials) {
        return (
            <div className="flex flex-col items-center justify-center h-full p-12 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mb-6 text-red-400">
                    <AlertCircle size={28} />
                </div>
                <h2 className="text-xl font-black text-slate-900 mb-2">Bullhorn niet geconfigureerd</h2>
                <p className="text-slate-500 text-sm">Ga naar <strong>Bullhorn Sync Settings</strong> om de credentials in te stellen.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-y-auto">

            {/* ── STAP 1: Verbinden ── */}
            <div className="m-5 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden shrink-0">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
                    <Link2 size={14} className="text-slate-400" />
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Stap 1 — API Verbinding (verplicht voor elke sessie)</span>
                </div>
                <div className="px-5 py-4 flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        {connectSuccess && authData ? (
                            <div className="flex items-center gap-2 text-emerald-700 text-sm font-bold">
                                <ShieldCheck size={16} className="shrink-0" />
                                Verbonden met Bullhorn — token actief
                                <span className="font-mono text-[10px] text-slate-400 ml-2 truncate max-w-[200px]">{authData.BhRestToken?.slice(0, 20)}...</span>
                            </div>
                        ) : connectError ? (
                            <div className="flex items-center gap-2 text-red-600 text-sm font-bold">
                                <AlertCircle size={16} className="shrink-0" />
                                {connectError}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500 font-medium">
                                Klik op <strong>Test &amp; Verbinden</strong> om een verse API-token op te halen van Bullhorn.
                                <span className="ml-1 text-slate-400 text-xs">Credentials worden geladen uit Bullhorn Sync Settings.</span>
                            </p>
                        )}
                    </div>
                    <button
                        onClick={handleConnect}
                        disabled={isConnecting}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-sm shrink-0 ${
                            connectSuccess
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                                : 'bg-slate-900 text-white hover:bg-black'
                        }`}
                    >
                        {isConnecting
                            ? <><Loader2 size={14} className="animate-spin" /> Verbinden...</>
                            : connectSuccess
                                ? <><RefreshCw size={14} /> Opnieuw verbinden</>
                                : <><Link2 size={14} /> Test &amp; Verbinden</>
                        }
                    </button>
                </div>
            </div>

            {/* ── STAP 2: Stats + sync knop ── */}
            {authData && (
                <>
                    {/* Header with sync button */}
                    <div className="px-5 pb-3 shrink-0 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center">
                                <GitPullRequest size={18} />
                            </div>
                            <div>
                                <h2 className="text-sm font-black text-slate-900 leading-none">Stap 2 — Pipeline Sync</h2>
                                <p className="text-[10px] font-medium text-slate-500 mt-0.5">Haalt JobSubmissions + Notes op per vacature → <code className="bg-slate-100 px-1 rounded font-mono">applications</code> tabel &amp; vacature data</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => loadVacancies()}
                                disabled={loading || running}
                                className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                title="Vacatures herladen"
                            >
                                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                            </button>
                            <button
                                onClick={syncDateAddedOnly}
                                disabled={loading || running || !authDataRef.current}
                                className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border bg-white text-slate-700 border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Sync alleen Bullhorn JobOrder.dateAdded → vacancies.data.bullhorn.dateAdded (geen inserts, geen status/title updates)"
                            >
                                <Clock size={12} />
                                Sync dateAdded
                            </button>
                            <button
                                onClick={() => setForceAll(f => !f)}
                                disabled={running}
                                className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border ${
                                    forceAll
                                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                                        : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                                }`}
                                title={forceAll ? 'Alles hersynchen (ook al gesynchroniseerde vacatures)' : 'Alleen nieuwe vacatures synchroniseren'}
                            >
                                <RefreshCw size={12} />
                                {forceAll ? 'Alles' : 'Alleen Nieuw'}
                            </button>
                            <button
                                onClick={runSync}
                                disabled={running || vacancies.length === 0 || !authDataRef.current}
                                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {running ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                                {running ? `Bezig...` : 'Start Pipeline Sync'}
                            </button>
                            {running && (
                                <button
                                    onClick={() => { stopRef.current = true; addLog('⏹ Stopverzoek ontvangen...', 'warn'); }}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-red-600 transition-all shadow-md"
                                >
                                    ⏹ Stop
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Stats bar */}
                    <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 px-5 pb-4 shrink-0">
                        {[
                            { label: 'Vacatures',      value: vacancies.length,    icon: <Briefcase size={15} />,   color: 'text-indigo-600 bg-indigo-50' },
                            { label: 'In DB',          value: existingAppCount,    icon: <Users size={15} />,       color: 'text-emerald-600 bg-emerald-50' },
                            { label: 'Overgeslagen',   value: skippedCount,        icon: <CheckCircle2 size={15} />,color: 'text-slate-500 bg-slate-100' },
                            { label: 'Submissions',    value: totalSubmissions,    icon: <BarChart2 size={15} />,   color: 'text-blue-600 bg-blue-50' },
                            { label: 'Notes',          value: totalNotes,          icon: <CheckCircle2 size={15} />,color: 'text-amber-600 bg-amber-50' },
                            { label: 'Nieuw Gesync',   value: totalSynced,         icon: <CheckCircle2 size={15} />,color: 'text-violet-600 bg-violet-50' },
                        ].map((s, i) => (
                            <div key={i} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 shadow-sm">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.color}`}>{s.icon}</div>
                                <div>
                                    <div className="text-xl font-black text-slate-900 leading-none">{s.value}</div>
                                    <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{s.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Progress bar */}
                    {running && (
                        <div className="px-5 pb-3 shrink-0">
                            <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{doneCount} / {vacancies.length} verwerkt</span>
                                    <span className="text-[10px] font-bold text-indigo-600">{progress}%</span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                    <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-1.5 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Table + Log */}
                    <div className="flex flex-col lg:flex-row gap-4 px-5 pb-5 flex-1 min-h-0">
                        {/* Vacancies table */}
                        <div className="flex-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Vacatures ({vacancies.length})</span>
                            </div>
                            <div className="overflow-y-auto" style={{ maxHeight: '420px' }}>
                                {loading ? (
                                    <div className="flex items-center justify-center py-12 text-slate-400">
                                        <Loader2 size={20} className="animate-spin mr-2" /> Laden...
                                    </div>
                                ) : vacancies.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-slate-400 text-sm">
                                        <Briefcase size={28} className="mb-3 opacity-30" />
                                        Geen vacatures met Bullhorn ID gevonden
                                    </div>
                                ) : (
                                    <table className="w-full text-left">
                                        <thead className="text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 sticky top-0 bg-white">
                                            <tr>
                                                <th className="px-4 py-2">Vacature</th>
                                                <th className="px-4 py-2">BH ID</th>
                                                <th className="px-4 py-2">Submissions</th>
                                                <th className="px-4 py-2">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {vacancies.map((v, i) => (
                                                <tr key={v.id} className={`transition-colors ${currentIdx === i && running ? 'bg-blue-50/60' : 'hover:bg-slate-50/40'}`}>
                                                    <td className="px-4 py-2 text-sm font-semibold text-slate-800 max-w-[200px] truncate">{v.title}</td>
                                                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{v.bullhorn_id}</td>
                                                    <td className="px-4 py-2 text-xs font-bold text-slate-600">
                                                        {v.submissionCount !== undefined ? v.submissionCount : '–'}
                                                        {v.synced !== undefined && v.synced > 0 && <span className="ml-1.5 text-emerald-600">+{v.synced}</span>}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${statusColor[v.status || 'idle']}`}>
                                                            {v.status === 'syncing' && <Loader2 size={8} className="animate-spin" />}
                                                            {v.status === 'done' && <CheckCircle2 size={8} />}
                                                            {v.status === 'error' && <AlertCircle size={8} />}
                                                            {statusLabel[v.status || 'idle']}
                                                        </span>
                                                        {v.error && <p className="text-[9px] text-red-500 mt-0.5 truncate max-w-[140px]">{v.error}</p>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        {/* Log panel */}
                        <div className="w-full lg:w-80 bg-slate-900 rounded-xl overflow-hidden flex flex-col shadow-xl">
                            <div className="px-4 py-2.5 border-b border-slate-700">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Logboek ({logs.length})</span>
                            </div>
                            <div className="flex-1 overflow-y-auto p-3 space-y-0.5 font-mono text-[10px]" style={{ maxHeight: '420px', minHeight: '160px' }}>
                                {logs.length === 0
                                    ? <div className="text-slate-600 text-center py-8">Nog geen logs...</div>
                                    : logs.map((log, i) => (
                                        <div key={i} className={`flex gap-2 leading-relaxed ${
                                            log.type === 'success' ? 'text-emerald-400' :
                                            log.type === 'error'   ? 'text-red-400' :
                                            log.type === 'warn'    ? 'text-amber-400' :
                                            'text-slate-400'
                                        }`}>
                                            <span className="text-slate-600 shrink-0">{log.time}</span>
                                            <span>{log.msg}</span>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default BullhornPipelineSync;
